import {
  buildQuantityOptions,
  isLineItemPurchasable,
} from 'components/ProductDetails';
import { notifyError } from 'config/setupBugsnag';
import getConfig from 'next/config';
import { Cart, IOrder, JsonApiDocument } from 'types';
import RestUtility from '../../../restUtility';
import { iteroEndpoint } from '../../../restUtility/restEndpoints';

export class IteroCartService {
  private restUtility: RestUtility;
  cartQueryFields: string;

  constructor() {
    this.restUtility = new RestUtility();
    this.cartQueryFields =
      'line_items,variants,variants.images,promotions&fields[image]=thumbnail_url,original_url';
  }

  async createITeroCart() {
    try {
      const response = await this.restUtility.post<IOrder>(
        `${iteroEndpoint}/cart?include=${this.cartQueryFields}&cart_type=itero`,
        {}
      );

      return this.buildCartForClient(response);
    } catch (error) {
      notifyError('ApiError:: createCart', error);
      throw error;
    }
  }

  async addScannerLineitem(
    variantId: string,
    quantity?: number,
    scannerType?: string,
    scannerServiceType?: string,
    itemPrice?: string
  ) {
    try {
      const item = {
        variant_id: variantId,
        quantity: quantity,
        item_price: itemPrice,
        scanner_type: scannerType,
        scanner_service_type: scannerServiceType,
      };
      const params = { line_items: [item] };
      const response = await this.restUtility.post<IOrder>(
        `${iteroEndpoint}/cart/add_itero_scanners?include=${this.cartQueryFields}&cart_type=itero`,
        params
      );
      return this.buildCartForClient(response);
    } catch (error) {
      notifyError('ApiError:: addLineitem', error);
      throw error;
    }
  }

  buildCartForClient(response: IOrder) {
    const { publicRuntimeConfig } = getConfig();
    const cart: Cart = {
      state: '',
      lineItems: [],
      total: 0,
      displayTotal: '',
      displaySubTotal: '',
      displayLineItemsTotalWithDiscounts: '',
      displayLineItemsTotalWithoutDiscount: '',
      lineItemSavings: '',
      shipmentSavings: '',
      orderSavings: '',
      itemCount: 0,
      taxTotal: '',
      displayTaxTotal: '',
      withholdingTaxTotal: '',
      shipTotal: '',
      displayShipTotal: '',
      isBulkDiscountEligible: false,
      displayBulkDiscountTotal: '',
      bulkDiscountTotal: 0,
      billToAddressId: '',
      shipToAddressId: '',
      checkoutStates: [],
      currency: '',
      promotions: [],
      email: '',
      isCustomClamShellOrder: false,
      totalRedeemPoints: '',
    };
    const lineItems = response.included?.filter(
      item => item.type === 'line_item'
    );
    const variants = response.included?.filter(item => item.type === 'variant');
    const images = response.included?.filter(item => item.type === 'image');
    const appliedPromotions = response.included?.filter(
      item => item.type === 'promotion'
    );

    const cartData = response.data;
    cart.state = cartData.attributes.state;
    cart.total = cartData.attributes.total;
    cart.displayTotal = cartData.attributes.display_total;
    cart.displaySubTotal = cartData.attributes.display_item_total;
    cart.displayLineItemsTotalWithDiscounts =
      cartData.attributes.display_line_items_total_after_discounts;
    cart.displayLineItemsTotalWithoutDiscount =
      cartData.attributes.display_line_items_total_before_discounts;
    cart.itemCount = cartData.attributes.item_count;
    cart.taxTotal = cartData.attributes.tax_amount;
    cart.displayTaxTotal = cartData.attributes.display_tax_amount;
    cart.withholdingTaxTotal = cartData.attributes.withholding_tax_amount ?? '';
    cart.shipTotal = cartData.attributes.ship_total;
    cart.displayShipTotal = cartData.attributes.display_ship_total;
    cart.isBulkDiscountEligible = cartData.attributes.is_bulk_discount_eligible;
    cart.displayBulkDiscountTotal =
      cartData.attributes.display_bulk_discount_amount;
    cart.bulkDiscountTotal = cartData.attributes.bulk_discount_amount;
    cart.shipToAddressId = cartData.relationships.shipping_address?.data?.id;
    cart.billToAddressId = cartData.relationships.billing_address?.data?.id;
    cart.checkoutStates = cartData.attributes.checkout_states;
    cart.currency = cartData.attributes.currency;
    cart.email = cartData.attributes.email;
    cart.clamshellTemplateId = cartData.attributes.design_id;
    cart.isCustomClamShellOrder = cartData.attributes.is_custom_clamshell_order;
    cart.lineItemSavings = cartData.attributes.display_line_items_savings;
    cart.shipmentSavings = cartData.attributes.display_shipment_savings;
    cart.orderSavings = cartData.attributes.display_order_savings;
    cart.totalRedeemPoints = cartData.attributes.total_redeem_points;

    if (appliedPromotions && appliedPromotions?.length > 0) {
      appliedPromotions.forEach(promo => {
        cart.promotions.push({
          promoCode: promo.attributes.code,
          promoTotal: promo.attributes.display_amount,
        });
      });
    }

    if (lineItems) {
      lineItems.forEach(lineItem => {
        const lineItemOriginalVariant = variants?.filter(
          variant => variant.id === lineItem.relationships.variant.data.id
        )[0];

        const skuListWithQuantityBasedPricing =
          publicRuntimeConfig.SKU_LIST_WITH_QUANTITY_BASED_PRICING.split(',');

        const lineItemImage = images?.filter(image => {
          return (
            image.id ===
            lineItemOriginalVariant?.relationships.images.data[0]?.id
          );
        })[0];

        const isProductPriceChanged =
          parseFloat(lineItem.attributes.price).toFixed(2) !==
            parseFloat(lineItemOriginalVariant?.attributes.price).toFixed(2) &&
          !this.isRewardsPointApplied(lineItem) &&
          !skuListWithQuantityBasedPricing.includes(
            lineItemOriginalVariant?.attributes.sku.toString()
          );

        const lineItemImageUrl = this.getThumbnailUrl(lineItemImage);

        const customProductQuantityLimit =
          lineItemOriginalVariant?.attributes.quantity_limit;

        cart.lineItems.push({
          id: lineItem.id,
          name: lineItem.attributes.name,
          slug: lineItem.attributes.slug,
          quantity: lineItem.attributes.quantity,
          quantityLimit: buildQuantityOptions(customProductQuantityLimit),
          total: lineItem.attributes.total,
          displayTotal: lineItem.attributes.display_total_after_bulk_discount,
          displayTotalWithoutDiscount:
            lineItem.attributes.display_total_without_adjustments,

          price: lineItem.attributes.price,
          displayPrice: lineItem.attributes.display_price,
          inStock: isLineItemPurchasable(
            lineItemOriginalVariant?.attributes.backorderable,
            lineItemOriginalVariant?.attributes.in_stock,
            lineItemOriginalVariant?.attributes.quantity_limit
          ),
          isOriginalProductPriceChanged: isProductPriceChanged,
          imageUrl: lineItemImageUrl,
          isHavingPromoCodeAdjustments:
            lineItem.attributes.is_having_promo_code_adjustments,
          isHavingBulkDiscountAdjustments:
            lineItem.attributes.is_having_bulk_discount_adjustments,
          isHavingSubsriptionAdjustments:
            lineItem.attributes.is_having_subscription_adjustments,
          isHavingAnyAdjustments:
            lineItem.attributes.is_having_subscription_adjustments ||
            lineItem.attributes.is_having_bulk_discount_adjustments ||
            lineItem.attributes.is_having_promo_code_adjustments ||
            lineItem.attributes.is_having_dso_discount_adjustments,
          discountPercentage: lineItem.attributes.discount_percentage,
          isHavingBulkDiscount: lineItem.attributes.is_having_bulk_discount,
          bulkDiscountPromoTitle: lineItem.attributes.bulk_discount_promo_title,
          bulkDiscountPromoDescription:
            lineItem.attributes.bulk_discount_promo_description,
          sku: lineItemOriginalVariant?.attributes.sku,
          isRewardPointsRedeemed: lineItem.attributes.checked,
          redeemedPoints: lineItem.attributes.redeem_points,
          discontinueOn: lineItemOriginalVariant?.attributes.discontinue_on,
          isCustomizable: lineItemOriginalVariant?.attributes.is_customizable,
          dhProjectId: lineItem.attributes.dh_project_id,
          couponCode: lineItem.attributes.coupon_code,
          displayDiscountAmount: lineItem.attributes.display_discount_amount,
          isHavingDSODiscount: lineItem.attributes.is_having_dso_discount,
          isHavingDSODiscountAdjustments:
            lineItem.attributes.is_having_dso_discount_adjustments,
          dsoDiscountPromoTitle: lineItem.attributes.dso_discount_promo_title,
          dsoDiscountPromoDescription:
            lineItem.attributes.dso_discount_promo_description,
        });
      });
    }
    return cart;
  }

  isRewardsPointApplied(lineItem: JsonApiDocument) {
    return lineItem.attributes.checked && lineItem.attributes.redeem_points > 0;
  }
  getThumbnailUrl(productImage: JsonApiDocument | undefined) {
    const { publicRuntimeConfig } = getConfig();
    return productImage
      ? `${publicRuntimeConfig.IMAGE_HOST}${productImage.attributes.thumbnail_url}`
      : '/images/noimage.jpeg';
  }
}
